import { ServiceFetcher, ServiceFetcherSettings } from '@decisions/server/ServiceFetcher';
import { IFormHostProps } from 'domains/designers/forms/formHostTypes';
import { enableMapSet } from 'immer';
import { RenderCallback, renderComponent } from 'utils/RenderingUtils';
import { IPropertyGridProps } from './components/property-editors/PropertyGrid/PropertyGrid';
import { useFolderActionsStore as FolderActionsStore } from 'domains/portal/ActionsStore';
import { getPortalStoreConnector } from 'domains/portal/PortalStoreConnector';
import { IBackButtonProps } from 'components/PageControls/BackButton';
import { IOSSCopyrightProps } from 'domains/portal/OSSCopyright/OSSCopyright';

export { renderConfirmActionDialog } from 'components/Dialogs/ConfirmActionDialog';
export { renderSuggestNextFormDialog } from 'components/Dialogs/SuggestNextFormDialog';
export { setIsMobile } from './config/PortalConfiguration';
export { setPortalSetup } from 'utils/PortalSetup';
export { unMount } from 'utils/RenderingUtils';
export { renderAlertOrInfoDialog, renderConfirmDialog } from 'components/Dialogs';
export { getPortalSettings, useSettingsStore as SettingsStore } from 'domains/portal/SettingsStore';

const scriptLocation = process.env.PUBLIC_URL; // write compile time value to local variable
// This tells webpack to use virtualPath as the base from which to load lazy-loaded assets.
// If we ever add additional entry-points, they will need the same treatment.
if (!DEV_SERVER) {
  __webpack_public_path__ = `${virtualPath}${scriptLocation}/`;
}

/**
 * "Common" here means common between mobile and desktop entry-points
 */

enableMapSet(); // need this once in the app to use immer to update maps: https://immerjs.github.io/immer/map-set/

/**
 * Configure the abstraction behind server proxies
 * @param settings for service fetcher
 */
export function setupServiceFetcher(settings: ServiceFetcherSettings) {
  ServiceFetcher.setup(settings);
}

export async function renderFormHost(props: IFormHostProps, container: HTMLElement, callback?: RenderCallback) {
  const { FormHostContainer } = await import('./domains/designers/forms/FormHostContainer');

  return renderComponent({
    container,
    component: <FormHostContainer {...props} />,
    callback,
    autoUnmount: true,
  });
}

/**
 * Handle for rendering the property grid into an HTML element.
 * @param container to house property grid component
 * @param props to render property grid with
 * @param callback when rendered
 */
export async function renderPropertyGrid(container: HTMLElement, props: IPropertyGridProps, callback?: RenderCallback) {
  // use webpack's "special" import syntax to asynchronously load React and PropertyGrid
  // in a separate chunks, and tell webpack what to name them all at once.

  // TODO use /* webpackPrefetch: true */ for React
  // if/when we get to webpack 4.6.0+
  // https://webpack.js.org/guides/code-splitting/#prefetchingpreloading-modules

  const { PropertyGrid } = await import(
    /* webpackChunkName: "dp-cmp-property-grid" */ './components/property-editors/PropertyGrid/PropertyGrid'
  );

  return renderComponent({
    container,
    component: <PropertyGrid {...props} />,
    callback,
    autoUnmount: false,
  });
}

export function getFolderActionHandlers() {
  return FolderActionsStore.getState();
}

/**
 * for conditionals that need to know whether Folder tree is present & initialized
 * @returns true of some folder tree is ready to receive updates, etc.
 */
export function isFolderTreeInitialized() {
  return getPortalStoreConnector().isFolderTreeInitialized();
}

export async function renderBackButton(props: IBackButtonProps, container: HTMLElement, callback: RenderCallback) {
  const { BackButton } = await import('components/PageControls/BackButton');

  return renderComponent({
    container,
    component: <BackButton {...props} />,
    autoUnmount: true,
    callback,
  });
}

export async function renderOssCopyright(props: IOSSCopyrightProps, container: HTMLElement, callback?: RenderCallback) {
  const { OSSCopyright } = await import('domains/portal/OSSCopyright/OSSCopyright');

  return renderComponent({
    container,
    component: <OSSCopyright {...props} />,
    callback,
    autoUnmount: true,
  });
}
